import { Map } from 'immutable'
import _toInteger from 'lodash/toInteger'
import _toNumber from 'lodash/toNumber'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import _isString from 'lodash/isString'
import { getBaseContentType } from '../content-type/index'

/**
 * Wire event names that dual-produce to Brooklyn eventLog (when auth JWT present).
 */
export const CONTENT_HIGHLIGHTS_EVENT_LOG_EVENTS = [
  'content_highlights_page_viewed',
  'content_highlights_session_opened',
  'content_highlights_search_submitted',
  'content_highlights_search_completed',
  'content_highlights_session_deleted',
  'content_highlights_clip_selected',
  'content_highlights_clip_started',
  'content_highlights_clip_ended',
  'content_highlights_options_changed',
  'content_highlights_nav_action',
]

const CONTENT_HIGHLIGHTS_EVENT_NAME_MAP = {
  content_highlights_page_viewed: 'ContentHighlightsPageViewed',
  content_highlights_session_opened: 'ContentHighlightsSessionOpened',
  content_highlights_search_submitted: 'ContentHighlightsSearchSubmitted',
  content_highlights_search_completed: 'ContentHighlightsSearchCompleted',
  content_highlights_session_deleted: 'ContentHighlightsSessionDeleted',
  content_highlights_clip_selected: 'ContentHighlightsClipSelected',
  content_highlights_clip_started: 'ContentHighlightsClipStarted',
  content_highlights_clip_ended: 'ContentHighlightsClipEnded',
  content_highlights_options_changed: 'ContentHighlightsOptionsChanged',
  content_highlights_nav_action: 'ContentHighlightsNavActionClicked',
}

function formatContentHighlightsGaiaEventName (eventName) {
  if (!eventName || !_isString(eventName)) {
    return undefined
  }
  return CONTENT_HIGHLIGHTS_EVENT_NAME_MAP[eventName]
}

function toOptionalInt (value) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return _toInteger(value)
}

function toOptionalBool (value) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return Boolean(value)
}

/**
 * Format enriched Content Highlights GA4 payload for Brooklyn eventLog mutation.
 * @param {Map} data - Enriched Immutable event from pushContentHighlightsGa4Event
 * @returns {Object} EventLogInput-shaped object
 */
export function formatContentHighlightsEventLogData (data = Map()) {
  if (!Map.isMap(data) || data.isEmpty()) {
    return {}
  }

  const rawEventName = data.get('event') || data.get('eventName')
  const formattedEventName = formatContentHighlightsGaiaEventName(rawEventName)
  if (!formattedEventName) {
    return {}
  }

  const contentIdRaw = data.get('content_id')
  const numericContentId = _toNumber(contentIdRaw)
  const hasValidContentId = numericContentId >= 1
  const contentType = hasValidContentId ? getBaseContentType(data.get('content_type')) : undefined

  const eventData = _omitBy({
    highlightSessionId: data.get('highlight_session_id') != null
      ? String(data.get('highlight_session_id'))
      : undefined,
    highlightCurationMode: data.get('curation_mode'),
    highlightDurationPreset: data.get('duration_preset'),
    highlightMaxResults: toOptionalInt(data.get('max_results')),
    highlightOneSpanPerVideo: toOptionalBool(data.get('one_span_per_video')),
    highlightContinuousPlayback: toOptionalBool(data.get('continuous_playback')),
    highlightLanguage: data.get('language'),
    highlightPromptLength: toOptionalInt(data.get('prompt_length')),
    highlightPromptWordCount: toOptionalInt(data.get('prompt_word_count')),
    highlightResultCount: toOptionalInt(data.get('result_count')),
    highlightHadResults: toOptionalBool(data.get('had_results')),
    highlightSearchState: data.get('state'),
    highlightClipIndex: toOptionalInt(data.get('clip_index')),
    highlightClipCount: toOptionalInt(data.get('clip_count')),
    highlightClipStartSec: toOptionalInt(data.get('clip_start_sec')),
    highlightClipEndSec: toOptionalInt(data.get('clip_end_sec')),
    highlightAction: data.get('action'),
    highlightOpenSource: data.get('open_source'),
    highlightSelectionMethod: data.get('selection_method'),
    highlightEndedVia: data.get('ended_via'),
    highlightView: data.get('view'),
    highlightNavAction: data.get('nav_action'),
    highlightOption: data.get('option'),
    highlightOptionValue: data.get('value') != null ? String(data.get('value')) : undefined,
    ...(hasValidContentId ? {
      contentId: numericContentId,
      contentType,
    } : {}),
    contentName: data.get('content_name'),
    mediaId: data.get('media_id') != null ? String(data.get('media_id')) : undefined,
    errorType: data.get('error_type'),
    uiLocation: data.get('ui_location'),
    uiElement: data.get('ui_element'),
    membershipStatus: data.get('membership_status'),
    userInTest: data.get('user_in_test'),
    userLanguage: data.get('user_language'),
    guestPhase: data.get('guest_phase'),
    appName: data.get('app_name'),
    appVersion: data.get('app_version'),
    allTestsCollated: data.get('all_tests_collated'),
    pageLocation: data.get('current_url'),
    externalId: data.get('gaia_uuid'),
    eventNumber: data.has('event_number') ? _toInteger(data.get('event_number')) : undefined,
  }, _isUndefined)

  return {
    eventName: formattedEventName,
    data: eventData,
  }
}
